/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useMemo } from 'react';
import { formatCurrency } from '../utils/formatters.ts';
import { bucketOrdersByDay, fillDailyRange } from '../utils/dashboardAggregations.ts';
import { useEarnedSincePayout, PayoutLike } from '../hooks/useEarnedSincePayout.ts';
import { useRecentUnpaidOrders } from '../hooks/useRecentUnpaidOrders.ts';
import SalesTrendChart, { TrendPoint } from './SalesTrendChart.tsx';
import Last30DaysChart from './Last30DaysChart.tsx';
import RecentSalesList, { RecentSaleEntry } from './RecentSalesList.tsx';
import MoneyRainAnimation from './MoneyRainAnimation.tsx';
import './SalesDashboardSection.css';

interface SalesDashboardSectionProps {
  selectedCollectionName: string | null;
  selectedHandle: string | undefined;
  // The highest order number covered by any of the selected collection's
  // payouts, or null if it has never had one (or its payouts predate this
  // field and haven't been backfilled yet) - narrows the earned-since-
  // payment fetch to just orders after that point, and also decides the
  // "Amount earned" vs "Amount earned since your last payment" label
  // below. Order-number based rather than date based - see
  // useEarnedSincePayout for why.
  lastOrderNumberPaid: number | null;
  payouts: PayoutLike[];
  payoutsLoading: boolean;
}

// Everything shown between the top of the page and the collection-scoped
// payout history below - shared by AdminPayouts (viewing any collection)
// and UserPayouts (viewing the logged-in seller's own collection), since
// the underlying numbers mean the same thing either way.
//
// Two different fetches feed this, deliberately kept apart so the slow
// one never blocks the fast one:
//  - earned-since-payment (+ the Total Sales/Total Orders cards, which
//    share its "since last payment" definition) needs the collection's
//    entire order history, so it's always the slower of the two.
//  - the trend chart, 30-day chart, and recent sales list only need a
//    recent window, so they load from a separate, much cheaper request
//    and render as soon as that comes back - independent of whether the
//    earned figure above has finished yet.
const SalesDashboardSection: React.FC<SalesDashboardSectionProps> = ({
  selectedCollectionName,
  selectedHandle,
  lastOrderNumberPaid,
  payouts,
  payoutsLoading
}) => {
  const hasPriorPayout = lastOrderNumberPaid !== null;

  const { earnedAmount, earnedLoading, unpaidOrders } = useEarnedSincePayout(
    selectedHandle,
    payouts,
    payoutsLoading,
    lastOrderNumberPaid
  );

  const { recentOrders, recentOrdersLoading } = useRecentUnpaidOrders(
    selectedHandle,
    payouts,
    payoutsLoading
  );

  // Total Sales / Total Orders share earned-since-payment's "since your
  // last payment" definition, so they share its data source (and its
  // load time) rather than the fast recent-window fetch below.
  const salesSinceLastPayment = useMemo(() => {
    const totalSales = unpaidOrders.reduce((sum, order) => {
      if (order.financialStatus?.toLowerCase() === 'refunded') return sum;
      return sum + (parseFloat(order.totalPrice?.toString()) || 0);
    }, 0);
    return { totalSales, totalOrders: unpaidOrders.length };
  }, [unpaidOrders]);

  const trendPoints = useMemo<TrendPoint[]>(() => {
    const dailyMap = bucketOrdersByDay(recentOrders);
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 29);
    const daily = fillDailyRange(dailyMap, start, end);

    return daily.map((bucket) => ({
      label: bucket.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullLabel: bucket.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      sales: bucket.salesTotal,
      orders: bucket.orderCount
    }));
  }, [recentOrders]);

  // Exactly 30 bars, oldest to newest, ending today - gaps on no-sales
  // days are filled with a zero-value bucket so the bar chart never skips
  // a day. Shares the same recent-window data (and the same 30-day
  // window) as the trend chart above it.
  const last30Days = useMemo(() => {
    const dailyMap = bucketOrdersByDay(recentOrders);
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 29);
    return fillDailyRange(dailyMap, start, end);
  }, [recentOrders]);

  const last20Sales = useMemo<RecentSaleEntry[]>(() => {
    return [...recentOrders]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 20)
      .map((order) => ({
        orderId: String(order.orderId),
        orderNumber: String(order.orderNumber),
        createdAt: order.createdAt,
        totalPrice: parseFloat(order.totalPrice?.toString()) || 0,
        financialStatus: order.financialStatus || 'unknown'
      }));
  }, [recentOrders]);

  // A small celebratory flourish, played once money has actually come in
  // since the last payment - never while that figure is still loading or
  // unknown, and never for a zero/negative amount. One bill per $10
  // earned, capped at 50, so a bigger payday literally rains more money.
  const moneyRainBillCount = earnedLoading || earnedAmount === null
    ? 0
    : Math.min(50, Math.floor(earnedAmount / 10));

  if (!selectedCollectionName) {
    return null;
  }

  return (
    <>
      {moneyRainBillCount > 0 && <MoneyRainAnimation billCount={moneyRainBillCount} />}

      <div className="earned-since-payout">
        <span className="earned-since-payout__label">
          {hasPriorPayout ? 'Amount earned since your last payment' : 'Amount earned'}
        </span>
        {earnedLoading ? (
          <span className="earned-since-payout__spinner" aria-label="Loading" />
        ) : earnedAmount === null ? (
          <span className="earned-since-payout__value earned-since-payout__value--error">Unable to load</span>
        ) : (
          <span className="earned-since-payout__value">{formatCurrency(earnedAmount)}</span>
        )}
      </div>

      <div className="quick-stats-row">
        <div className="quick-stat-card quick-stat-card--sales">
          <div className="quick-stat-card__labels">
            <span className="quick-stat-card__label">Total Sales</span>
            <span className="quick-stat-card__sublabel">since your last payment</span>
          </div>
          {earnedLoading ? (
            <span className="quick-stat-card__spinner" aria-label="Loading" />
          ) : (
            <span className="quick-stat-card__value">{formatCurrency(salesSinceLastPayment.totalSales)}</span>
          )}
        </div>
        <div className="quick-stat-card quick-stat-card--orders">
          <div className="quick-stat-card__labels">
            <span className="quick-stat-card__label">Total Orders</span>
            <span className="quick-stat-card__sublabel">since your last payment</span>
          </div>
          {earnedLoading ? (
            <span className="quick-stat-card__spinner" aria-label="Loading" />
          ) : (
            <span className="quick-stat-card__value">{salesSinceLastPayment.totalOrders}</span>
          )}
        </div>
      </div>

      <SalesTrendChart points={trendPoints} rangeLabel="Last 30 Days" loading={recentOrdersLoading} />
      <Last30DaysChart days={last30Days} loading={recentOrdersLoading} />
      <RecentSalesList sales={last20Sales} loading={recentOrdersLoading} />
    </>
  );
};

export default SalesDashboardSection;
